import type { StripeError } from "@stripe/stripe-js";

import { paywallCheckoutAttemptsApi } from "@/react/api/paywallCheckoutAttemptsApi";
import { ApiError } from "@/react/config/CustomErrors";

import { CheckoutError } from "../../CheckoutError";

const DEFERRED_CANCEL_MAX_TRIES = 3;
type CancellationOutcome = "canceled" | "navigate" | "reconcile";

const requestCancellation = async (
  attemptId: number,
  processorError: StripeError,
): Promise<CancellationOutcome> => {
  const response = await paywallCheckoutAttemptsApi.cancel(attemptId, {
    code: processorError.code,
    decline_code: processorError.decline_code,
    message: processorError.message ?? "",
  });
  if (response?.failure) {
    throw CheckoutError.fromPaywallError(
      "submitSetupIntent.cancelDeferredAttempt",
      { ...response, failure: response.failure },
    );
  }
  return response?.navigate ? "navigate" : "canceled";
};

const isRetryableConflictBody = (body: unknown): body is { retryable: true } =>
  typeof body === "object" &&
  body !== null &&
  "retryable" in body &&
  body.retryable === true;

const isAmbiguousCancellationError = (error: unknown) => {
  if (error instanceof CheckoutError) {
    return false;
  }
  if (!(error instanceof ApiError)) {
    return true;
  }

  const isContention =
    error.status === 409 && isRetryableConflictBody(error.body);
  return error.status >= 500 || isContention;
};

const retryDelaySeconds = (error: unknown) =>
  error instanceof ApiError
    ? Number(error.response?.headers.get("Retry-After") ?? 1)
    : 1;

export const cancelDeferredAttempt = async (
  attemptId: number,
  processorError: StripeError,
): Promise<CancellationOutcome> => {
  for (let tryIndex = 0; tryIndex < DEFERRED_CANCEL_MAX_TRIES; tryIndex++) {
    try {
      return await requestCancellation(attemptId, processorError);
    } catch (error: unknown) {
      if (!isAmbiguousCancellationError(error)) {
        throw CheckoutError.fromUnknownError("cancelDeferredAttempt", error);
      }
      if (tryIndex === DEFERRED_CANCEL_MAX_TRIES - 1) {
        return "reconcile";
      }

      await new Promise(resolve =>
        setTimeout(resolve, Math.max(retryDelaySeconds(error), 0) * 1000),
      );
    }
  }

  return "reconcile";
};
